import React, { useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const UploadResultsModal = ({
  isOpen,
  onClose,
  uploadSuccesses = [],
  uploadErrors = [],
  uploadDuplicates = [],
  onAcceptDuplicates,
  onRejectDuplicates,
  isProcessingDuplicates = false,
  onRetryUpload,
  /** 'leads' | 'preleads' — only affects user-facing copy */
  fileEntityLabel = 'leads',
  /** When false, duplicate rows are shown read-only (no accept/reject). */
  duplicateActionsEnabled = true
}) => {
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  const userId = Cookies.get('userId');

  // Handler for selecting/deselecting individual duplicate
  const handleDuplicateSelect = (duplicate, isSelected) => {
    if (isSelected) {
      setSelectedDuplicates((prev) => [...prev, duplicate]);
    } else {
      setSelectedDuplicates((prev) => prev.filter((item) => item.row !== duplicate.row));
    }
  };

  // Handler for selecting all duplicates
  const handleSelectAllDuplicates = (isSelected) => {
    if (isSelected) {
      setSelectedDuplicates([...uploadDuplicates]);
    } else {
      setSelectedDuplicates([]);
    }
  };

  // Handler for accepting individual duplicate
  const handleAcceptIndividualDuplicate = (duplicate) => {
    const payload = {
      duplicates: [duplicate]
    };
    onAcceptDuplicates(payload);
  };

  // Handler for rejecting individual duplicate
  const handleRejectIndividualDuplicate = (duplicate) => {
    setSelectedDuplicates((prev) => prev.filter((item) => item.row !== duplicate.row));
    onRejectDuplicates([duplicate]);
  };

  // Handler for accepting selected duplicates
  const handleAcceptSelectedDuplicates = () => {
    if (selectedDuplicates.length === 0) {
      toast.error('Please select at least one duplicate to accept');
      return;
    }

    const payload = {
      duplicates: selectedDuplicates
    };

    onAcceptDuplicates(payload);
  };

  // Handler for rejecting selected duplicates
  const handleRejectSelectedDuplicates = () => {
    if (selectedDuplicates.length === 0) {
      toast.error('Please select at least one duplicate to reject');
      return;
    }

    onRejectDuplicates(selectedDuplicates);
    setSelectedDuplicates([]);
  };

  // Reset state when modal closes
  const handleClose = () => {
    setSelectedDuplicates([]);
    onClose();
  };

  // Don't render if modal is not open
  if (!isOpen) return null;

  return (
    <Modal
      width={60}
      confirmOnClose={true}
      isDirty={true}
      name="Upload Results"
      onClose={handleClose}>
      <div className="flex flex-col justify-between items-start gap-4">
        <div className="flex flex-col gap-4 w-full">
          {/* Success Section */}
          {uploadSuccesses && uploadSuccesses.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800 mb-3">
                The following {fileEntityLabel} were added successfully.
              </p>
              <ul className="space-y-2">
                {uploadSuccesses?.map((success, index) => (
                  <li key={index} className="text-sm text-green-700 flex items-start">
                    <span className="text-green-500 mr-2">•</span>
                    <span>{success.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Duplicates Section */}
          {uploadDuplicates && uploadDuplicates.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800 font-medium mb-3">
                {duplicateActionsEnabled ? (
                  <>
                    The following {fileEntityLabel} are already present in the database. Select
                    which ones you want to duplicate:
                  </>
                ) : (
                  <>
                    The following {fileEntityLabel} look like duplicates or conflicts. Remove or
                    fix these rows in your file, then upload again.
                  </>
                )}
              </p>
              {duplicateActionsEnabled && (
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 text-sm text-yellow-800 font-medium">
                      <input
                        type="checkbox"
                        checked={
                          selectedDuplicates.length === uploadDuplicates.length &&
                          uploadDuplicates.length > 0
                        }
                        onChange={(e) => handleSelectAllDuplicates(e.target.checked)}
                        className="rounded"
                      />
                      Select All ({uploadDuplicates.length})
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleRejectSelectedDuplicates}
                      disabled={selectedDuplicates.length === 0 || isProcessingDuplicates}
                      className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1">
                      ✗ Reject Selected ({selectedDuplicates.length})
                    </button>
                    <button
                      onClick={handleAcceptSelectedDuplicates}
                      disabled={selectedDuplicates.length === 0 || isProcessingDuplicates}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1">
                      {isProcessingDuplicates
                        ? 'Processing...'
                        : `✓ Accept Selected (${selectedDuplicates.length})`}
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-3 max-h-60 overflow-y-auto">
                {uploadDuplicates.map((duplicate, index) => (
                  <div key={index} className="bg-white border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {duplicateActionsEnabled && (
                          <input
                            type="checkbox"
                            checked={selectedDuplicates.some(
                              (selected) => selected.row === duplicate.row
                            )}
                            onChange={(e) => handleDuplicateSelect(duplicate, e.target.checked)}
                            className="mt-1 rounded"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{duplicate.name}</span>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              Row {duplicate.row}
                            </span>
                          </div>
                          <p className="text-sm text-yellow-700 mb-2">{duplicate.message}</p>
                          <div className="text-xs text-gray-600 flex items-center gap-4">
                            <span>
                              <strong>Stage:</strong> {duplicate.stage}
                            </span>
                            <span>
                              <strong>Phone:</strong> {duplicate.data?.phone_number || 'N/A'}
                            </span>
                            <span>
                              <strong>Email:</strong> {duplicate.data?.email || 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                      {duplicateActionsEnabled && (
                        <div className="flex gap-2 ml-3">
                          <button
                            onClick={() => handleAcceptIndividualDuplicate(duplicate)}
                            className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors flex items-center gap-1"
                            disabled={isProcessingDuplicates}
                            title="Accept this duplicate">
                            ✓ Accept
                          </button>
                          <button
                            onClick={() => handleRejectIndividualDuplicate(duplicate)}
                            className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors flex items-center gap-1"
                            disabled={isProcessingDuplicates}
                            title="Reject this duplicate">
                            ✗ Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Errors Section */}
          {uploadErrors && uploadErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 mb-3">
                The following errors were found in your uploaded file. Please fix these issues and
                try uploading again:
              </p>
              <ul className="space-y-2">
                {uploadErrors?.map((error, index) => (
                  <li key={index} className="text-sm text-red-700 flex items-start">
                    <span className="text-red-500 mr-2">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end items-center space-x-4 mt-4">
            <Button type="button" onClick={handleClose} displayType="secondary">
              Close
            </Button>
            {uploadErrors && uploadErrors.length > 0 && (
              <Button
                type="button"
                onClick={onRetryUpload}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                Try Again
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UploadResultsModal;
